import React, { createRef, useState } from "react";
import {
  deleteRegistrationDocument,
  DocumentType,
  downloadRegistrationDocument,
  getRegistrationDocumentContent,
  getRegistrationDocumentsForRegistrationId,
  uploadStateDocument,
} from "../../Base/Services/RegistrationDocumentService";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@material-ui/core";
import useAlerts from "../../Base/AlertProvider";
import { GetApp, Visibility, Close, Delete } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";

export default function RegistrationAdoptionDocuments({ registration }) {
  const { addErrorAlert } = useAlerts();
  const [documents, setDocuments] = useState(null);
  const [uploadDocumentType, setUploadDocumentType] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const fileInputRef = createRef();
  const [documentToPreview, setDocumentToPreview] = React.useState();
  const [imagePreviewContent, setImagePreviewContent] = React.useState();

  // If registrationId provided, then try to load the existing identity document(s)
  React.useEffect(() => {
    if (registration.registrationId) {
      getRegistrationDocumentsForRegistrationId(registration.registrationId, [
        DocumentType.STATE_DOCUMENT,
        DocumentType.BIRTH_CERTIFICATE,
      ])
        .then(setDocuments)
        .catch(() => addErrorAlert("There was an error loading OVRS's documents"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration.registrationId]);

  function previewDocument(imageDocument) {
    setDocumentToPreview(imageDocument);
    getRegistrationDocumentContent(imageDocument)
      .then(setImagePreviewContent)
      .catch(() => addErrorAlert("Error loading image"));
  }

  return (
    <div>
      <div style={{ display: "flex" }}>
        <Typography variant="body1">
          <b style={{ marginRight: 4 }}>Adoption Documents:</b>
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={7} container direction="column" justify="space-around" alignItems="flex-start">
            <input
              type="file"
              id="file-to-upload-input"
              name="file-to-upload-input"
              hidden
              accept="image/*,.pdf"
              ref={fileInputRef}
              onChange={(e) => {
                const file = fileInputRef.current?.files ? fileInputRef.current?.files[0] : null;
                if (file) {
                  const MAX_FILE_SIZE_MB = 25;
                  if (file.size > 25 * 1000000) {
                    addErrorAlert("Error uploading document. Maximum file size is " + MAX_FILE_SIZE_MB + "MB");
                    return;
                  }
                  if (!file.type || !(file.type === "application/pdf" || file.type.startsWith("image/"))) {
                    addErrorAlert("Invalid file type. Only PDF & image files are supported");
                    return;
                  }
                  setUploadInProgress(true);
                  uploadStateDocument(registration.registrationId, file, uploadDocumentType)
                    .then((registrationDocument) => {
                      setDocuments([...documents, registrationDocument]);
                    })
                    .finally(() => {
                      setUploadInProgress(false);
                      e.target.value = "";
                    });
                }
              }}
            />
            <div id="documents-container">
              {documents ? (
                documents.length > 0 ? (
                  <List>
                    {documents.map((doc, i) => (
                      <ListItem>
                        <ListItemText>{doc.documentName}</ListItemText>
                        <ListItemIcon>
                          <Tooltip title="Download" aria-label="Download">
                            <IconButton
                              aria-label="download"
                              onClick={() => {
                                downloadRegistrationDocument(doc);
                              }}
                            >
                              <GetApp />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" aria-label="Delete">
                            <IconButton
                              onClick={() => {
                                if (window.confirm("Are you sure you want to remove this document?")) {
                                  deleteRegistrationDocument(doc).then(() => {
                                    const updatedDocList = [...documents];
                                    updatedDocList.splice(
                                      updatedDocList.findIndex(
                                        (document) => document.registrationDocumentId === doc.registrationDocumentId
                                      ),
                                      1
                                    );
                                    setDocuments(updatedDocList);
                                  });
                                }
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </ListItemIcon>
                        {(doc.documentContentType.startsWith("image/") ||
                          doc.documentContentType === "application/pdf") && (
                          <ListItemIcon>
                            <Tooltip title="Preview" aria-label="Preview">
                              <IconButton onClick={() => previewDocument(doc)} aria-label="preview">
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </ListItemIcon>
                        )}
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1">No OVRS uploaded documents found.</Typography>
                )
              ) : (
                <Skeleton />
              )}
              <Dialog
                open={Boolean(documentToPreview)}
                onClose={() => setDocumentToPreview(null)}
                fullWidth
                maxWidth="lg"
              >
                {documentToPreview && (
                  <>
                    <DialogTitle>
                      <span style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Image Preview: {documentToPreview.documentName}</span>
                        <IconButton size="small" color="default" onClick={() => setDocumentToPreview(null)}>
                          <Close />
                        </IconButton>
                      </span>
                    </DialogTitle>
                    <DialogContent style={{ textAlign: "center" }}>
                      {imagePreviewContent ? (
                        documentToPreview.documentContentType === "application/pdf" ? (
                          <embed
                            src={`data:${documentToPreview.documentContentType};base64,${imagePreviewContent}`}
                            style={{ minHeight: 600, width: "100%" }}
                          />
                        ) : (
                          <img
                            alt={documentToPreview?.documentName}
                            src={`data:${documentToPreview.documentContentType};base64,${imagePreviewContent}`}
                          />
                        )
                      ) : (
                        <Skeleton
                          variant="rect"
                          style={{ height: 450, width: 600, marginLeft: "auto", marginRight: "auto" }}
                        />
                      )}
                    </DialogContent>
                  </>
                )}
              </Dialog>
            </div>
          </Grid>
          <Grid item sm={5} container direction="column" alignItems="flex-start" spacing={1} style={{ padding: 16 }}>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                size="large"
                disabled={uploadInProgress}
                onClick={() => {
                  setUploadDocumentType(DocumentType.STATE_DOCUMENT);
                  fileInputRef.current && fileInputRef.current.click();
                }}
              >
                Upload Document
              </Button>
            </Grid>
            <Grid item>&nbsp;</Grid>
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                size="large"
                disabled={uploadInProgress}
                onClick={() => {
                  setUploadDocumentType(DocumentType.BIRTH_CERTIFICATE);
                  fileInputRef.current && fileInputRef.current.click();
                }}
              >
                Upload Certificate
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
